import { hashCode } from "flavr-common";
import { ModelPath } from "../modelPath/ModelPath";
/**
 * Utility class that stores a mapping between paths and uids
 */
export class ModelUIDRegistry {
    constructor() {
        this.uidToPath = new Map();
        this.pathToUID = new Map();
        this.uidToParent = new Map();
        this.uidToChildren = new Map();
        /**
         * Temporary map that lists all children, that only know their parents path but don't
         * know its uid since the parent's uid wasn't registered yet. Will be cleared once
         * the parent could be resolved
         */
        this.childrenWaitingForParent = new Map();
        const rootPath = ModelPath.rootPath();
        const uid = hashCode(rootPath.toString());
        this.uidToPath.set(uid, rootPath);
        this.pathToUID.set(rootPath.toString(), uid);
    }
    /**
     * Hashes the ModelPath to create a unique uid
     */
    createUID(path) {
        return hashCode(path.toString());
    }
    /// Mapping between path and uid ///
    /**
     * Adds a mapping between the uid and its path
     */
    addEntry(uid, path) {
        if (this.uidToPath.has(uid))
            return;
        this.uidToPath.set(uid, path);
        this.pathToUID.set(path.toString(), uid);
        this.updateParent(uid, path);
        this.updateChildren(uid, path);
    }
    /**
     * Removes the mapping between the uid and its path
     */
    removeEntry(uidOrPath) {
        if (uidOrPath instanceof ModelPath) {
            const uid = this.pathToUID.get(uidOrPath.toString());
            if (uid === undefined)
                return;
            const path = uidOrPath;
            this.pathToUID.delete(path.toString());
            this.uidToPath.delete(uid);
            this.uidToParent.delete(uid);
            this.uidToChildren.delete(uid);
            this.childrenWaitingForParent.delete(path.toString());
        }
        else {
            const path = this.uidToPath.get(uidOrPath);
            if (path === undefined)
                return;
            const uid = uidOrPath;
            this.pathToUID.delete(path.toString());
            this.uidToPath.delete(uid);
            this.uidToParent.delete(uid);
            this.uidToChildren.delete(uid);
            this.childrenWaitingForParent.delete(path.toString());
        }
    }
    /**
     * Returns the uid that was registered for the path. If no mapping exists then undefined is returned instead
     */
    getUIDFromPath(path) {
        return this.pathToUID.get(path.toString());
    }
    /**
     * Returns the path that was registered for the uid. If no mapping exists then undefined is returned instead
     */
    getPathFromUID(uid) {
        return this.uidToPath.get(uid);
    }
    /// Utility functions ///
    /**
     * Returns the ModelPath to the parent of the provided uid or undefined if either the model or the parent don't exist
     */
    getParent(uid) {
        const path = this.getPathFromUID(uid);
        if (path === undefined)
            return undefined;
        return path.getParent();
    }
    /**
     * Returns the uid to the parent of the provided uid or undefined if either the model or the parent don't exist
     */
    getParentUID(uid) {
        const parentPath = this.getParent(uid);
        if (parentPath === undefined)
            return undefined;
        return this.getUIDFromPath(parentPath);
    }
    /**
     * Returns the ModelPath to the room of the provided uid or undefined if either the model or the room don't exist
     */
    getRoom(uid) {
        const path = this.getPathFromUID(uid);
        if (path === undefined)
            return undefined;
        return path.getRoom();
    }
    /**
     * Returns the uid to the room of the provided uid or undefined if either the model or the room don't exist
     */
    getRoomUID(uid) {
        const roomPath = this.getRoom(uid);
        if (roomPath === undefined)
            return undefined;
        return this.getUIDFromPath(roomPath);
    }
    /**
     * Returns all child uids of the model of the provided uid or an empty array if the model doesn't exist
     */
    getChildren(uid) {
        var _a;
        return (_a = this.uidToChildren.get(uid)) !== null && _a !== void 0 ? _a : [];
    }
    /// Manage parent and children ///
    updateParent(uid, path) {
        var _a;
        const parentPath = path.getParent();
        if (parentPath === undefined)
            return;
        // If no parent is registered yet, we will wait until a model for the parent path is registered and
        // then add all the children that are waiting for their parent
        const parentUID = this.pathToUID.get(parentPath.toString());
        if (parentUID === undefined) {
            let waitingChildren = this.childrenWaitingForParent.get(parentPath.toString());
            if (waitingChildren === undefined) {
                waitingChildren = [];
                this.childrenWaitingForParent.set(parentPath.toString(), waitingChildren);
            }
            waitingChildren.push(uid);
            return;
        }
        // Add parent
        this.uidToParent.set(uid, parentUID);
        // Add this model as child of its parent
        const children = (_a = this.uidToChildren.get(parentUID)) !== null && _a !== void 0 ? _a : [];
        children.push(uid);
        this.uidToChildren.set(parentUID, children);
    }
    updateChildren(uid, path) {
        var _a;
        const waitingChildrenUIDs = this.childrenWaitingForParent.get(path.toString());
        if (waitingChildrenUIDs === undefined)
            return;
        const childUIDs = (_a = this.uidToChildren.get(uid)) !== null && _a !== void 0 ? _a : [];
        for (const waitingChildUID of waitingChildrenUIDs) {
            childUIDs.push(waitingChildUID);
        }
        this.uidToChildren.set(uid, childUIDs);
        // Remove waiting children
        this.childrenWaitingForParent.delete(path.toString());
    }
}
export const modelUIDRegistry = new ModelUIDRegistry();
